import React, { useMemo, useState } from 'react';
import { AppBar, Box, Button, IconButton, Toolbar, Tabs } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import InsertChart from '@mui/icons-material/InsertChart';
import Menu from '@mui/icons-material/Menu';
import { useLocation, NavLink } from 'react-router-dom';
import { useModal } from 'mui-modal-provider';

import DrawerMenu from '../DrawerMenu';
import FluicityLogo from '../FluicityLogo';
import SignUpButton from '../SignUpButton';
import LanguageMenu from '../LanguageMenu/LanguageMenu';
import NavbarLinkTab from '../NavBarLinkTab';
import NavBarDropdownMenu from '../NavBarDropdownMenu';
import Hidden from '../../DesignSystem/Hidden';
import { Roles } from '../../../Utils/Types';
import { isAccessingViaCustomDomain, isPageSpecificToASpace } from '../../../Utils';
import ProfileMenu from '../ProfileMenu';
import { MenuItemToMuiMenuItemProps, generateMuiMenuItems } from '../../../Utils/Space/navigation';
import { useErrorBoundary } from '../../ErrorBoundary/ErrorBoundary';
import EntitySubscriptionModal from '../../EntitySubscriptionModal';

function NavBar({
  role,
  isLoggedIn,
  pictureS3Key,
  filename,
  space,
  fullName,
  verified,
  initials,
  isLoading,
}: any) {
  const intl = useIntl();
  const { showModal } = useModal();
  const location = useLocation();
  const { hasError } = useErrorBoundary();
  const [open, setOpen] = useState(false);
  // Tracks which group tab's dropdown is open: maps tab value -> anchor element.
  const [openMenuValue, setOpenMenuValue] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const hideNavElements =
    location.pathname === '/home' || (location.pathname === '/' && !isAccessingViaCustomDomain());
  const isAdministrationButtonsVisible = isPageSpecificToASpace() && role !== Roles.CITIZEN;
  const isMultilingual = space?.locales?.length > 1;

  const LanguageButton = (
    <Box
      key="language-menu"
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingInlineEnd: 1,
      }}
    >
      <LanguageMenu />
    </Box>
  );

  // Real navigation entries: rendered as <Tab> children inside <Tabs>. MUI v9's
  // Tabs runs roving-tabindex / indicator logic over its children and ONLY
  // tolerates <Tab>s — any non-Tab child triggers an infinite render loop.
  const tabLinks: MenuItemToMuiMenuItemProps[] = [];

  const spaceMenuItems = useMemo(() => generateMuiMenuItems(space, intl), [space, intl]);
  if (!hideNavElements) {
    tabLinks.push(...spaceMenuItems);
  }

  // Non-Tab widgets: rendered in the Toolbar flex AFTER the <Tabs>, never inside
  // it. This is the root-cause fix for the MUI v9 loop. The dashboard button
  // belongs here (right-aligned, after the spacer), not among the nav tabs.
  const endWidgets: React.ReactNode[] = [];
  if (!hideNavElements) {
    if (isLoggedIn) {
      if (isAdministrationButtonsVisible) {
        endWidgets.push(
          <Box key="dashboard" sx={{ display: 'flex', alignItems: 'center', paddingInlineEnd: 1 }}>
            <Button
              component={NavLink}
              to={`/${space.slug}/dashboard`}
              startIcon={<InsertChart />}
              color="inherit"
              size="large"
              data-testid="dashboard-button"
            >
              {intl.formatMessage({ id: 'NAVBAR.DASHBOARD' })}
            </Button>
          </Box>,
        );
      }

      if (isMultilingual) {
        endWidgets.push(LanguageButton);
      }

      if (space.slug && !space.is_current_user_subscribed) {
        endWidgets.push(
          <Box
            key="joinbutton"
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={() => showModal(EntitySubscriptionModal)}
              color="primary"
              variant="contained"
              size="medium"
            >
              <FormattedMessage id="BUTTONS.JOIN" />
            </Button>
          </Box>,
        );
      }

      if (space.slug) {
        endWidgets.push(
          <Box
            key="profile-menu"
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ProfileMenu
              fullName={fullName}
              initials={initials}
              userPictureS3Key={pictureS3Key}
              filename={filename}
              verified={verified}
            />
          </Box>,
        );
      }
    } else {
      if (isMultilingual) {
        endWidgets.push(LanguageButton);
      }

      endWidgets.push(
        <Box
          key="signup"
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SignUpButton />
        </Box>,
      );
    }
  }

  const handleOpenMenu = (value: string) => (anchorEl: HTMLElement) => {
    setOpenMenuValue(value);
    setMenuAnchorEl(anchorEl);
  };

  const handleCloseMenu = () => {
    setOpenMenuValue(null);
    setMenuAnchorEl(null);
  };

  const isGroupLink = (link: MenuItemToMuiMenuItemProps) =>
    Array.isArray(link.menuItems) && link.menuItems.length > 0;

  // Each Tab needs a unique `value` (MUI maps value -> index internally).
  // Group tabs all carry href "#", so we key them by id instead to avoid
  // collisions and to keep them from ever matching the active route.
  const tabValueFor = (link: MenuItemToMuiMenuItemProps) =>
    isGroupLink(link) ? String(link.id) : link.href;

  const renderNavLink = (link: MenuItemToMuiMenuItemProps, index: number) => {
    const { href, label, ariaLabel, component, id, menuItems, ...rest } = link;
    const value = tabValueFor(link);

    return (
      <NavbarLinkTab
        {...(component ? { component, href } : { to: href })}
        label={label}
        value={value}
        menuItems={menuItems}
        onOpenMenu={isGroupLink(link) ? handleOpenMenu(value as string) : undefined}
        key={`${id}-${index}`}
        aria-label={ariaLabel || label}
        {...rest}
      />
    );
  };

  if (hasError) return null;

  const tabValue =
    tabLinks.find(
      (link) => link.href && link.href !== '#' && location.pathname.startsWith(link.href),
    )?.href || false;

  return (
    <>
      <AppBar position="fixed" color="inherit" component="nav">
        <Toolbar style={{ flex: 1, overflow: 'visible' }}>
          <Box sx={{ mr: 3 }}>
            <FluicityLogo currentEntity={space} isLoading={isLoading} />
          </Box>

          {space.slug && !hideNavElements && (
            <Hidden breakpoint="lg" direction="down">
              <Tabs
                value={tabValue}
                sx={{
                  height: 64,
                  root: { height: 64 },
                  overflow: 'visible',
                  '& .MuiTabs-scroller': {
                    overflow: 'visible !important',
                  },
                }}
              >
                {tabLinks.map(renderNavLink)}
              </Tabs>

              {tabLinks.filter(isGroupLink).map((link) => {
                const value = tabValueFor(link) as string;
                return (
                  <NavBarDropdownMenu
                    key={`menu-${link.id}`}
                    id={`navbar-menu-items-${value}`}
                    anchorEl={openMenuValue === value ? menuAnchorEl : null}
                    onClose={handleCloseMenu}
                    menuItems={link.menuItems}
                  />
                );
              })}

              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  alignItems: 'center',
                  justifyContent: 'end',
                  height: 64,
                }}
              >
                {endWidgets}
              </Box>
            </Hidden>
          )}

          <Hidden breakpoint="lg" direction="up">
            <Box
              sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'end',
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => setOpen(true)}
                size="large"
              >
                <Menu />
              </IconButton>
            </Box>
          </Hidden>
        </Toolbar>
      </AppBar>

      <DrawerMenu
        space={space}
        role={role}
        userPictureS3Key={pictureS3Key}
        open={open}
        handleClose={() => setOpen(false)}
        fullName={fullName}
        initials={initials}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
}

export default NavBar;
